import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("pwa_dismissed") === "1") return;
    // Only greet visitors on public/marketing pages — never cover the working app or critical actions.
    if (/^\/(login|register|pay|join|tournament|app|parent|portal)/.test(window.location.pathname)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // Greet the user after they've had a moment with the hero — never immediately.
    const t = setTimeout(() => setShow(true), 4000);
    return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem("pwa_dismissed", "1"); };
  const [hint, setHint] = useState(false);
  const install = async () => {
    if (!deferred) { setHint(true); return; }
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;
  const ios = isIOS();

  return (
    <div className="fixed z-[70] bottom-3 inset-x-3 sm:inset-x-auto sm:right-4 sm:bottom-24 sm:w-[22rem] animate-fade-up" data-testid="pwa-install-modal">
      <div className="rounded-2xl border border-border bg-card/95 backdrop-blur-md p-3.5 shadow-2xl">
        <div className="flex items-center gap-3">
          <img src="/logo192.png" alt="SphereHyx" className="h-11 w-11 rounded-xl border border-border shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-heading font-bold text-sm leading-tight">Get the SphereHyx app</p>
            <p className="text-xs text-muted-foreground truncate">Full-screen, one tap from your home screen.</p>
          </div>
          {ios ? (
            <button onClick={() => setHint((h) => !h)} className="h-9 px-3 rounded-lg brand-gradient text-white text-sm font-semibold shrink-0 hover:opacity-90 transition-opacity" data-testid="pwa-install-btn">Add</button>
          ) : (
            <button onClick={install} className="h-9 px-3 rounded-lg brand-gradient text-white text-sm font-semibold shrink-0 inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity" data-testid="pwa-install-btn"><Download size={15} /> Install</button>
          )}
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" data-testid="pwa-dismiss-btn" aria-label="Close"><X size={18} /></button>
        </div>

        {ios && hint && (
          <div className="mt-3 rounded-xl bg-accent/60 p-3 text-xs" data-testid="pwa-ios-hint">
            <p className="flex items-center gap-1.5 text-muted-foreground">1. Tap the <Share size={14} className="inline text-primary" /> Share button</p>
            <p className="flex items-center gap-1.5 text-muted-foreground mt-1">2. Choose <Plus size={13} className="inline text-primary" /> <b className="text-foreground">Add to Home Screen</b></p>
          </div>
        )}
        {!ios && hint && (
          <div className="mt-3 rounded-xl bg-accent/60 p-3 text-xs text-muted-foreground" data-testid="pwa-menu-hint">
            Open your browser menu <b className="text-foreground">⋮</b> and choose <b className="text-foreground">"Install app"</b> or <b className="text-foreground">"Add to Home screen"</b>.
          </div>
        )}
      </div>
    </div>
  );
}
